/**
 * State for the collaboration ("design together") first run.
 *
 * `CampaignLandingGate` calls {@link enterCollabOnboarding} once when a
 * paid-social visitor qualifies (see `./landingFlow.ts`). From then on
 * this module is the single source of truth for:
 *
 *   - whether the flow is active on this device (persisted, so a reload
 *     keeps the checklist instead of dropping the visitor into the
 *     standard UI mid-way),
 *   - which checklist steps are done and when,
 *   - the invite link produced by "Start together".
 *
 * Step completion is driven by product events observed through
 * {@link subscribeTrackedEvents} — the canvas and share code don't know
 * this module exists. The "joined" step is the exception: presence is
 * observed by the checklist component, which calls
 * {@link recordRemoteEditSeen}.
 */

import { useSyncExternalStore } from "react";
import { getFirstTouch, getLastTouch } from "./attribution";
import { seedLandingRoom, type DemoRoomId } from "./demoRooms";
import { isInAppBrowser } from "./inAppBrowser";
import { landingUtmContent } from "./landingFlow";
import { exitSharedRoom } from "./openRoom";
import { useDesign } from "./store";
import {
  addBreadcrumb,
  setUserProperty,
  subscribeTrackedEvents,
  track,
} from "./telemetry";

export type ChecklistStep = "drag" | "invite" | "joined" | "ai";
export const CHECKLIST_STEPS: readonly ChecklistStep[] = ["drag", "invite", "joined", "ai"];

export interface CollabOnboardingState {
  /** The flow was entered on this device. */
  active: boolean;
  startedAt: number | null;
  /** The full-screen intro was answered ("Try it together" / "Start my
   *  own room"). The guide waits for it. */
  introSeenAt: number | null;
  utmContent: string | null;
  /** Which starter design was seeded. */
  starterId: DemoRoomId | null;
  starterRoomId: string | null;
  /** Completion timestamps per step; `null` = not yet. */
  steps: Record<ChecklistStep, number | null>;
  /** Checklist card closed by the user. */
  dismissed: boolean;
  inviteLink: string | null;
  inviteCreatedAt: number | null;
  sharedRoomId: string | null;
  remoteEditSeen: boolean;
  /** Set once every checklist step is done (the guide's "You're all set"
   *  state). `onboarding_completed` fires exactly once when this flips. */
  completedAt: number | null;
}

const STORAGE_KEY = "room-canvas:onboarding:collab";

function initial(): CollabOnboardingState {
  return {
    active: false,
    startedAt: null,
    introSeenAt: null,
    utmContent: null,
    starterId: null,
    starterRoomId: null,
    steps: { drag: null, invite: null, joined: null, ai: null },
    dismissed: false,
    inviteLink: null,
    inviteCreatedAt: null,
    sharedRoomId: null,
    remoteEditSeen: false,
    completedAt: null,
  };
}

function load(): CollabOnboardingState {
  if (typeof window === "undefined") return initial();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return initial();
    const parsed = JSON.parse(raw) as Partial<CollabOnboardingState>;
    if (!parsed || typeof parsed !== "object") return initial();
    const base = initial();
    return {
      ...base,
      ...parsed,
      // Flows started before the intro existed are already past it.
      introSeenAt:
        parsed.introSeenAt === undefined ? (parsed.startedAt ?? null) : parsed.introSeenAt,
      steps: { ...base.steps, ...(parsed.steps ?? {}) },
    };
  } catch {
    return initial();
  }
}

let state: CollabOnboardingState = load();
const listeners = new Set<() => void>();

function persist(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode — in-memory state still works this session */
  }
}

function update(patch: Partial<CollabOnboardingState>): void {
  state = { ...state, ...patch };
  persist();
  for (const l of listeners) l();
}

export function getCollabOnboarding(): CollabOnboardingState {
  return state;
}

export function subscribeCollabOnboarding(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useCollabOnboarding(): CollabOnboardingState {
  return useSyncExternalStore(subscribeCollabOnboarding, getCollabOnboarding, getCollabOnboarding);
}

export function isCollabOnboardingActive(): boolean {
  return state.active;
}

export function completedStepCount(s: CollabOnboardingState = state): number {
  return CHECKLIST_STEPS.filter((k) => s.steps[k] != null).length;
}

/**
 * Seed the starter room, flip the flow on, and emit the funnel head.
 * Idempotent: a second call while already active is a no-op. Returns
 * false if the canvas already had a room (nothing was changed).
 */
export function enterCollabOnboarding(opts?: { utmContent?: string | null }): boolean {
  if (state.active) return true;
  const search = typeof window === "undefined" ? "" : window.location.search;
  const utmContent =
    opts?.utmContent ?? landingUtmContent(search, getLastTouch() ?? getFirstTouch());
  const seeded = seedLandingRoom(utmContent);
  if (!seeded) return false;
  const inApp = isInAppBrowser();
  update({
    active: true,
    startedAt: Date.now(),
    utmContent,
    starterId: seeded.id,
    starterRoomId: seeded.roomId,
  });
  ensureEventBridge();
  setUserProperty("first_run", "collab");
  setUserProperty("in_app_browser", inApp);
  track("collab_onboarding_shown", { utm_content: utmContent, in_app_browser: inApp });
  addBreadcrumb("collab-onboarding:entered", { utm_content: utmContent });
  return true;
}

const secondsSince = (t: number | null, now: number) =>
  t != null ? Math.max(0, Math.round((now - t) / 1000)) : null;

/** Tick a checklist step (once). */
export function completeChecklistStep(step: ChecklistStep): void {
  if (!state.active || state.steps[step] != null) return;
  const now = Date.now();
  update({ steps: { ...state.steps, [step]: now } });
  track("onboarding_step_completed", {
    flow: "collab",
    step,
    seconds_since_start: secondsSince(state.startedAt, now),
  });
  // When the last step lands, the guide reaches its "You're all set" state.
  // Emit the terminal funnel event exactly once — this is what the
  // acquisition funnel measures and what the collab flow never fired before.
  if (state.completedAt == null && completedStepCount() === CHECKLIST_STEPS.length) {
    update({ completedAt: now });
    track("onboarding_completed", {
      flow: "collab",
      steps_done: CHECKLIST_STEPS.length,
      seconds_since_start: secondsSince(state.startedAt, now),
    });
  }
}

export function dismissChecklist(): void {
  if (!state.active || state.dismissed) return;
  update({ dismissed: true });
  track("onboarding_guide_done", { flow: "collab", steps_done: completedStepCount() });
}

/** Whether the full-screen intro should be on screen. */
export function isCollabIntroPending(s: CollabOnboardingState = state): boolean {
  return s.active && !s.dismissed && s.introSeenAt == null;
}

/** The visitor chose "Try it together" on the intro. */
export function acceptCollabIntro(): void {
  if (!state.active || state.introSeenAt != null) return;
  update({ introSeenAt: Date.now() });
  track("collab_intro_choice", { choice: "together" });
}

/**
 * Leave the guided flow.
 *
 *   - `skip`: close the guide and keep the starter room.
 *   - `own_room`: close the guide and swap in a blank room so the
 *     visitor lands on the Shape view's "Start your room" chooser. A live
 *     shared session is left first so the shared room isn't wiped.
 */
export function exitCollabOnboarding(method: "own_room" | "skip"): void {
  if (!state.active) return;
  const fromIntro = state.introSeenAt == null;
  const stepsDone = completedStepCount();
  update({
    dismissed: true,
    introSeenAt: state.introSeenAt ?? Date.now(),
  });
  if (fromIntro && method === "own_room") {
    track("collab_intro_choice", { choice: "own_room" });
  }
  track("onboarding_exited", { flow: "collab", method, steps_done: stepsDone });
  if (method === "own_room") {
    exitSharedRoom();
    useDesign.getState().resetAll();
  }
}

/** Bring a dismissed guide back (Settings → Getting started guide). */
export function reopenChecklist(): void {
  if (!state.active || !state.dismissed) return;
  update({ dismissed: false, introSeenAt: state.introSeenAt ?? Date.now() });
}

/** True while there is something left for a reopened guide to show. */
export function hasRemainingSteps(s: CollabOnboardingState = state): boolean {
  return CHECKLIST_STEPS.some((k) => s.steps[k] == null);
}

/** "Start together" produced a shareable link. */
export function recordInviteLink(link: string, sharedRoomId: string): void {
  update({ inviteLink: link, sharedRoomId, inviteCreatedAt: state.inviteCreatedAt ?? Date.now() });
  completeChecklistStep("invite");
}

/** A real collaborator showed up in presence. */
export function recordPartnerJoined(): void {
  completeChecklistStep("joined");
}

/** The inviter watched a real collaborator commit an edit. */
export function recordRemoteEditSeen(roomId: string): void {
  if (!state.active || state.remoteEditSeen) return;
  const since =
    state.inviteCreatedAt != null
      ? Math.max(0, Math.round((Date.now() - state.inviteCreatedAt) / 1000))
      : null;
  update({ remoteEditSeen: true });
  completeChecklistStep("joined");
  track("collab_first_remote_edit_seen", { room_id: roomId, seconds_since_invite: since });
}

// ---------------------------------------------------------------------------
// Event bridge — product events → checklist steps
// ---------------------------------------------------------------------------

let bridgeUnsub: (() => void) | null = null;

/**
 * Subscribe once to the telemetry stream. Runs for the life of the page
 * whenever the flow is active; cheap enough not to bother tearing down.
 */
export function ensureEventBridge(): void {
  if (bridgeUnsub) return;
  bridgeUnsub = subscribeTrackedEvents((name, props) => {
    if (!state.active) return;
    if (name === "furniture_moved" && props?.source === "user") {
      completeChecklistStep("drag");
    } else if (name === "invite_link_created") {
      // `recordInviteLink` completes the step with the link attached;
      // this catches invites minted from the Share sheet instead.
      completeChecklistStep("invite");
    } else if (name === "ai_layout_applied") {
      completeChecklistStep("ai");
    }
  });
}

// Boot: if a previous page load entered the flow, keep listening.
if (state.active) ensureEventBridge();

/** Test-only: wipe memory + storage. */
export function _resetCollabOnboardingForTests(): void {
  if (bridgeUnsub) bridgeUnsub();
  bridgeUnsub = null;
  state = initial();
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  listeners.clear();
}
